<script lang="ts" setup>
import { onUnmounted, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useShipStore } from '@/stores/ship'

import GenericRowField from '@/components/form/GenericRowField.vue'

import type { FieldTypes } from '@/components/form/types/type.type'
import ShipTypeSelector from '../form/ShipTypeSelector.vue'
import type { ShipType } from '@/stores/type/ship.type'

const shipStore = useShipStore()
const { newItem, createUpdateDialog, writeLoading, fields } = storeToRefs(shipStore)

const formRef = ref()
const isValid = ref(false)

const shipFieldTypes: FieldTypes<ShipType> = {
	ship_class: {},
	capacity: {},
	length_overall: {},
	breadth: {},
	depth_main_deck_to_keel: {},
	laden_draught: {},
	ballasted_draught: {},
	forward_parallel_body_length: {},
	aftward_parallel_body_length: {},
	has_armour_param: {}
}

const armourFieldKeys: FieldTypes<ShipType> = {
	main_engine_power: {},
	main_engine_number: {},
	main_engine_diameter: {},
	main_engine_distance_between: {},
	main_engine_distance_tip_keel: {},
	main_engine_distance_ship_stern: {},
	rudder: {},
	main_engine_type: {},

	transverse_thruster_power: {},
	transverse_thruster_number: {},
	transverse_thruster_diameter: {},
	transverse_thruster_distance_between: {},
	transverse_thruster_distance_tip_keel: {},
	transverse_thruster_duct_length: {},
	transverse_thruster_type: {}
}

const hasHelpText = (field: string) => {
	return !!fields.value?.[field]?.help_text
}

const emit = defineEmits(['save'])

watch(createUpdateDialog, (value: boolean) => {
	if (!value) {
		newItem.value = null
	}
})

const save = async () => {
	await formRef.value.validate()
	if (isValid.value) {
		emit('save')
	}
}

onUnmounted(() => {
	newItem.value = null
	shipStore.setCurrentItem(null)
})
</script>

<template>
	<v-dialog v-model="createUpdateDialog" width="120vh" persistent>
		<v-card class="pa-3">
			<v-card-title>Create ship</v-card-title>
			<div v-if="newItem && fields" class="overflow-y-auto px-4" style="height: 80%">
				<v-form v-model="isValid" ref="formRef">
					<span class="sub-header-font">Base parameters</span>

					<GenericRowField
						:field="fields.name"
						:disabled="fields.name.read_only === true"
						v-model="newItem"
						:has-help-text="hasHelpText"
						inline
						equal-columns
					/>
					<ShipTypeSelector v-model="newItem" inline equal-columns />
					<GenericRowField
						v-for="[key, value] in Object.entries(shipFieldTypes)"
						:key="key"
						:field="fields[key]"
						:disabled="value.read_only === true"
						:type="value.type"
						v-model="newItem"
						:has-help-text="hasHelpText"
						:label="value.label"
						inline
						equal-columns
					/>
					<template v-if="newItem.has_armour_param">
						<div class="mt-2">
							<span class="sub-header-font">ARMOUR parameters</span>
							<GenericRowField
								v-for="[key, value] in Object.entries(armourFieldKeys)"
								:key="key"
								:field="fields[key]"
								:disabled="value.read_only === true"
								:type="value.type"
								v-model="newItem"
								:has-help-text="hasHelpText"
								:label="value.label"
								inline
								equal-columns
							/>
						</div>
					</template>
				</v-form>
			</div>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn
					:disabled="writeLoading"
					color="primary"
					class="text-none"
					variant="text"
					@click="shipStore.closeCreateUpdateDialog()"
				>
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					class="text-none"
					variant="text"
					:loading="writeLoading"
					@click="save"
				>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
